import { Coordinate, Entity } from "game-common/models"
import { AreaProjectileView } from "game-common/charged_weapon"
import { BLEND_MODES, Graphics } from "pixi.js"
import { emberCoreFeedback } from "../../../../effects/ember_core_feedback"
import { FORGE } from "../effects/weapon_charge"
import { BaseRenderable } from "./base_renderable"

/**
 * An Ember Core charge, from the muzzle to the crater. Three acts.
 *
 *   FLIGHT -- a molten capsule trailing fire, with a shadow running along the ground under it. The
 *     shadow is what sells the height; without it a bright dot slides across the floor.
 *   FUSE -- the 350ms the player has to read the danger and get out of it. This is the only part of
 *     the effect with a job beyond looking good, so it is built around ONE promise: the bright ring
 *     is exactly the circle the server will damage. A collapsing inner ring counts the fuse down
 *     inside it, and the strobe quickens as the time runs out.
 *   BLAST -- flash, shockwave, a torn fireball, flung embers. Over in 350ms, because the next thing
 *     the player needs to see is what survived.
 *
 * TWO LAYERS, AND THE REASON IS THE WHOLE LOOK OF IT. `glow` is an ADDITIVE child; the base Graphics
 * is normal. Fire has to be additive. Built as one normal-blend Graphics, every hot colour was just
 * a translucent orange wash over green grass, and the result read as a dust cloud with a grey ring
 * round it -- pale khaki polygons, not an explosion. Additive orange over ANY ground goes yellow and
 * then white where it piles up, which is what fire does. So: anything that emits light goes on
 * `glow`, and only things that block it -- the capsule's ground shadow, the smoke afterwards, the
 * dark backing under the warning ring -- stay on the base layer.
 *
 * NOTHING DARK OUTLIVES THE BLAST. The server deletes the projectile the instant the blast phase
 * ends, so any mark still painted on the final frame does not fade, it pops. An early version held a
 * scorch ellipse at full strength through the blast and it read as a shadow appearing after the
 * explosion and then vanishing. A crater that genuinely lingers would have to outlive the entity,
 * which means a ground-decal owner this effect does not have.
 *
 * AUTHORITATIVE GEOMETRY IS NEVER SQUASHED. The server's hit test is a true circle in world x/y
 * (ChargedWeapons.detonate), so the warning ring and the shockwave are true circles too. Only the
 * decorative ground art is flattened into the projection. A player standing just outside the ring
 * they can see has to actually be safe.
 *
 * All timing is relative: the server's remainingMs is re-anchored to local time on every message and
 * interpolated down between them. No clock sync, and no timer that can outlive the room.
 */

// The capsule flies this far above the ground point the server tracks. Its shadow stays on the
// ground, so the gap between them is the only cue for how high the shot is.
const FLIGHT_LIFT = 13
// How long the muzzle flash burns after the shot leaves. It is drawn at the ORIGIN, not on the
// moving projectile -- a flash that travels with the shell is a comet, not a discharge.
const MUZZLE_MS = 190
// Flung debris: count, and how far past the blast rim the furthest piece gets.
const DEBRIS = 16
const DEBRIS_REACH = 1.45
// Tongues around the fireball's edge. Prime-ish against DEBRIS so the two scatters never line up.
const TONGUES = 14
// Where the ground splits under a burning fuse. Uneven on purpose: evenly spaced spokes draw a
// wagon wheel. Five, not six, for the same reason.
const CRACK_ANGLES = [0.35, 1.5, 2.55, 3.7, 5.2]

/** Deterministic per-shot scatter: same shot draws the same debris every frame, no stored arrays. */
const scatter = (seed: number) => {
    const x = Math.sin(seed * 127.1 + 311.7) * 43758.5453
    return x - Math.floor(x)
}

export class AreaProjectile extends BaseRenderable {
    /** Everything that emits light. Additive, so fire over grass goes hot instead of muddy. */
    private glow = new Graphics()

    private view?: AreaProjectileView
    private receivedAt = 0
    private spawnedAt = 0
    private origin: Coordinate = { x: 0, y: 0 }
    private at: Coordinate = { x: 0, y: 0 }
    // Smoothed travel direction, for the trail. Derived from successive positions rather than sent:
    // the server already publishes the path, and re-deriving it costs nothing.
    private heading = { x: 1, y: 0 }
    private announced = { fired: false, landed: false, detonated: false }

    constructor(entity: Entity) {
        super()
        this.glow.blendMode = BLEND_MODES.ADD
        this.addChild(this.glow)
        this.spawnedAt = Date.now()
        this.origin = { ...entity.location }
        this.at = { ...entity.location }
        this.doMovement(entity)
    }

    getYOffset() { return 0 }

    doMovement(entity?: Entity) {
        const view = entity?.areaProjectile
        const now = Date.now()
        if (view !== this.view) {
            this.view = view
            this.receivedAt = now
        }
        if (entity) {
            const dx = entity.location.x - this.at.x, dy = entity.location.y - this.at.y
            const moved = Math.hypot(dx, dy)
            if (moved > 0.5) this.heading = { x: dx / moved, y: dy / moved }
            this.at = { ...entity.location }
        }
        this.clear()
        this.glow.clear()
        if (!view) return
        const remaining = Math.max(0, view.remainingMs - now + this.receivedAt)
        const progress = view.durationMs ? Math.max(0, Math.min(1, 1 - remaining / view.durationMs)) : 1

        if (!this.announced.fired) {
            this.announced.fired = true
            emberCoreFeedback.fired(this.origin)
        }
        if (view.phase !== "flight" && !this.announced.landed) {
            this.announced.landed = true
            emberCoreFeedback.landed(this.at)
        }
        if (view.phase === "blast" && !this.announced.detonated) {
            this.announced.detonated = true
            emberCoreFeedback.detonated(this.at)
        }

        if (view.phase === "blast") {
            this.drawBlast(view, progress, now)
            return
        }
        this.drawMuzzleFlash(now)
        if (view.phase === "fuse") this.drawFuse(view, progress, now)
        this.drawCapsule(view.phase, progress, now)
    }

    /**
     * The discharge, left behind at the muzzle. Drawn in the projectile's local space by offsetting
     * back to the origin, so it stays welded to the spot the shot was fired from while the shell
     * that carries this Graphics flies away from it.
     */
    private drawMuzzleFlash = (now: number) => {
        const life = 1 - (now - this.spawnedAt) / MUZZLE_MS
        if (life <= 0) return
        const g = this.glow
        const x = this.origin.x - this.at.x, y = this.origin.y - this.at.y - 8
        const fade = life * life
        g.lineStyle(0)
        g.beginFill(FORGE.ember, 0.35 * fade)
        g.drawCircle(x, y, 10 + 26 * (1 - life))
        g.endFill()
        g.beginFill(FORGE.flame, 0.5 * fade)
        g.drawCircle(x, y, 5 + 13 * (1 - life))
        g.endFill()
        g.beginFill(FORGE.hot, 0.8 * fade)
        g.drawCircle(x, y, 2 + 5 * (1 - life))
        g.endFill()
        // Four spits of flame thrown back out of the housing, along the firing line.
        g.lineStyle(2 * life, FORGE.flame, 0.6 * fade)
        for (let i = 0; i < 4; i++) {
            const spread = (i - 1.5) * 0.42
            const dx = this.heading.x * Math.cos(spread) - this.heading.y * Math.sin(spread)
            const dy = this.heading.x * Math.sin(spread) + this.heading.y * Math.cos(spread)
            const reach = (10 + 20 * (1 - life)) * (0.6 + 0.4 * scatter(i * 7 + 1))
            g.moveTo(x, y)
            g.lineTo(x + dx * reach, y + dy * reach)
        }
    }

    /** The capsule: shadow on the floor, fire behind it, molten shell, white-hot centre. */
    private drawCapsule = (phase: AreaProjectileView["phase"], progress: number, now: number) => {
        const g = this.glow
        const flying = phase === "flight"
        // On the ground the shell settles, flattens and beats faster as the fuse burns down.
        const beat = flying ? 1 : 1 + 0.16 * Math.sin(now / (70 - 35 * progress))
        const lift = flying ? FLIGHT_LIFT : 5
        const r = (flying ? 6 : 6.5) * beat
        // The one thing here that blocks light rather than emitting it, so it stays off the glow.
        this.lineStyle(0)
        this.beginFill(FORGE.scorch, flying ? 0.3 : 0.38)
        this.drawEllipse(0, 2, r * 1.25, r * 0.5)
        this.endFill()
        g.lineStyle(0)
        if (flying) {
            // The trail: three shrinking, dimming puffs laid back down the flight line, plus loose
            // sparks shedding sideways. Cheaper and steadier than a particle pool, and it cannot
            // leak when the room tears down mid-flight.
            for (let i = 1; i <= 3; i++) {
                const back = i * 9
                const t = 1 - i / 4
                g.beginFill(i === 1 ? FORGE.flame : FORGE.blaze, 0.4 * t)
                g.drawCircle(-this.heading.x * back, -this.heading.y * back - lift + i,
                    r * (1.05 - 0.2 * i) + Math.sin(now / 50 + i) * 0.8)
                g.endFill()
            }
            for (let i = 0; i < 4; i++) {
                const age = (now / 150 + scatter(i * 31 + 3)) % 1
                const back = 6 + age * 26
                const side = (scatter(i * 17 + 5) - 0.5) * 16 * age
                g.beginFill(FORGE.gold, 0.8 * (1 - age))
                g.drawCircle(-this.heading.x * back - this.heading.y * side,
                    -this.heading.y * back + this.heading.x * side - lift + age * 6, 1.6 * (1 - age) + 0.4)
                g.endFill()
            }
        }
        g.beginFill(FORGE.blaze, 0.34)
        g.drawCircle(0, -lift, r * 2.4 + Math.sin(now / 65) * 1.5)
        g.endFill()
        g.beginFill(FORGE.blaze, 0.6)
        g.drawCircle(0, -lift, r * 1.35)
        g.endFill()
        g.beginFill(FORGE.flame, 0.9)
        g.drawCircle(0, -lift, r * 0.85)
        g.endFill()
        g.beginFill(FORGE.hot, 1)
        g.drawCircle(0, -lift - r * 0.2, r * 0.42)
        g.endFill()
    }

    /**
     * The warning. The outer ring is the server's blast radius, exactly; everything else is drawn
     * inside it.
     *
     * Three independent reads of the same countdown, because one of them will be the one a given
     * player notices: the ring STROBES faster as the fuse runs out, an inner ring COLLAPSES from the
     * rim to the centre, and light CRACKS outward through the ground until it reaches the edge.
     *
     * The rim is drawn twice: a dark line on the base layer and a hot one on the glow. The dark pass
     * is what keeps it legible on pale ground (sand, the camp's trodden dirt, cave rock), where an
     * additive line alone washes out -- the same trick the player ring plays with its contour.
     */
    private drawFuse = (view: AreaProjectileView, progress: number, now: number) => {
        const g = this.glow
        const r = view.radius
        // Strobe period falls from about 190ms to 60ms across the fuse.
        const strobe = 0.5 + 0.5 * Math.sin(now / (30 - 20 * progress))
        this.lineStyle(0)
        this.beginFill(FORGE.scorch, 0.05 + 0.1 * progress)
        this.drawEllipse(0, 0, r, r * 0.52)
        this.endFill()
        this.lineStyle(4, FORGE.scorch, 0.4)
        this.drawCircle(0, 0, r)

        g.lineStyle(0)
        g.beginFill(FORGE.blaze, 0.09 + 0.12 * progress)
        g.drawCircle(0, 0, r)
        g.endFill()
        // Cracks reaching for the rim. Flattened into the ground plane: these are in the dirt.
        // Deliberately NOT evenly spaced and deliberately an odd number -- six at 60 degrees drew a
        // wagon wheel, which is the same trap the Bonecaller's grave mark documents.
        for (let i = 0; i < CRACK_ANGLES.length; i++) {
            const a = CRACK_ANGLES[i]
            const reach = r * progress
            g.lineStyle(1.3 + 1.1 * scatter(i * 19 + 2), FORGE.blaze, 0.45 + 0.45 * progress)
            g.moveTo(0, 0)
            for (let step = 1; step <= 3; step++) {
                const t = step / 3, wobble = Math.sin(i * 5.1 + step * 2.3) * 0.26 * (1 - t)
                g.lineTo(Math.cos(a + wobble) * reach * t, Math.sin(a + wobble) * reach * t * 0.75)
            }
        }
        // The collapsing fuse ring.
        g.lineStyle(2, FORGE.gold, 0.5 + 0.4 * progress)
        g.drawCircle(0, 0, r * (1 - progress))
        // The damage edge: a wide dim halo under a hard bright line, both at exactly `radius`.
        g.lineStyle(7, FORGE.blaze, 0.24 + 0.3 * strobe)
        g.drawCircle(0, 0, r)
        g.lineStyle(2.5, FORGE.gold, 0.7 + 0.3 * strobe)
        g.drawCircle(0, 0, r)
        g.lineStyle(0)
        for (let i = 0; i < 12; i++) {
            const a = i * Math.PI / 6 + now / 1400
            g.beginFill(FORGE.hot, 0.5 + 0.5 * strobe)
            g.drawCircle(Math.cos(a) * r, Math.sin(a) * r, 1.8)
            g.endFill()
        }
    }

    /**
     * A ragged ring of points at `radius`, for one shell of the fireball. Every shell uses the same
     * generator with a different seed so they tear in different places, and the same seed gives the
     * same shape on every frame -- the fireball grows, it does not boil.
     */
    private tongues = (radius: number, seed: number): number[] => {
        const points: number[] = []
        for (let i = 0; i < TONGUES; i++) {
            const a = i * Math.PI * 2 / TONGUES
            const reach = radius * (0.78 + 0.42 * scatter(i * 13 + seed))
            points.push(Math.cos(a) * reach, Math.sin(a) * reach)
        }
        return points
    }

    /**
     * Detonation, in one pass over four overlapping lives: a flash that is gone in the first fifth,
     * a shockwave that overruns the damage edge, a fireball torn into tongues, and debris thrown past
     * the rim. Smoke comes in underneath as the light goes, and is the only dark thing here.
     */
    private drawBlast = (view: AreaProjectileView, progress: number, now: number) => {
        const g = this.glow
        const r = view.radius
        const fade = 1 - progress
        const ease = 1 - (1 - progress) ** 3
        g.lineStyle(0)

        // Fireball, torn rather than round. A plain circle is what made the old blast read as a
        // bubble; the radial noise is what makes it read as fire coming apart.
        const body = r * (0.26 + 0.44 * ease)
        // An OPAQUE heart first, on the base layer. Additive alone is a tint -- you could read the
        // grass and a haystack straight through the middle of the fireball, which is the one place
        // fire is least transparent. This is what the glow then blooms out of.
        //
        // It is small and it dies early, by design. Held at full strength into the second half of
        // the blast it stopped being a core and became a flat yellow sticker with visible polygon
        // facets sitting on top of the glow.
        const heart = Math.max(0, 1 - progress / 0.5) ** 1.3
        if (heart > 0) {
            this.lineStyle(0)
            this.beginFill(FORGE.flame, 0.9 * heart)
            this.drawPolygon(this.tongues(body * 0.42, 50))
            this.endFill()
        }
        for (const [scale, color, alpha] of [[1.3, FORGE.blaze, 0.34], [1.0, FORGE.blaze, 0.5],
            [0.66, FORGE.flame, 0.55], [0.34, FORGE.gold, 0.6]] as [number, number, number][]) {
            g.beginFill(color, alpha * fade)
            g.drawPolygon(this.tongues(body * scale, scale * 100))
            g.endFill()
        }

        // Shockwave. Expands past the damage edge on purpose -- the ring that MATTERED was the fuse
        // warning, and this one is the aftermath, so overrunning it reads as force rather than lying
        // about reach.
        const wave = r * (0.25 + 1.0 * ease)
        g.lineStyle(11 * fade, FORGE.blaze, 0.34 * fade)
        g.drawCircle(0, 0, wave)
        g.lineStyle(2.5 * fade + 0.5, FORGE.hot, 0.9 * fade)
        g.drawCircle(0, 0, wave)

        // The flash: enormous, white, and gone by a fifth of the way through.
        const flash = Math.max(0, 1 - progress / 0.2)
        if (flash > 0) {
            g.lineStyle(0)
            g.beginFill(FORGE.blaze, 0.6 * flash)
            g.drawCircle(0, 0, r * (0.5 + 0.55 * (1 - flash)))
            g.endFill()
            g.beginFill(FORGE.hot, 0.9 * flash)
            g.drawCircle(0, 0, r * 0.34 * flash + 7)
            g.endFill()
        }

        // Debris, thrown out and arcing down. Seeded per index, so it is the same throw every frame.
        g.lineStyle(0)
        for (let i = 0; i < DEBRIS; i++) {
            const a = i * Math.PI * 2 / DEBRIS + scatter(i * 3 + 11) * 0.4
            const speed = 0.55 + 0.45 * scatter(i * 5 + 23)
            const travel = r * DEBRIS_REACH * speed * ease
            const hop = Math.sin(Math.min(1, progress / speed) * Math.PI) * 16 * speed
            g.beginFill(scatter(i * 9 + 7) > 0.6 ? FORGE.hot : FORGE.flame, fade)
            g.drawCircle(Math.cos(a) * travel, Math.sin(a) * travel * 0.85 - hop,
                (0.9 + 1.6 * scatter(i * 11 + 3)) * fade)
            g.endFill()
        }

        // Smoke, last and lowest contrast: it is what is left when the light has gone. The only dark
        // shape in the blast, and it has to be gone by progress 1 -- see the note at the top.
        if (progress > 0.4) {
            const smoke = (progress - 0.4) / 0.6
            this.lineStyle(0)
            for (let i = 0; i < 3; i++) {
                const a = i * 2.094 + 0.7
                this.beginFill(FORGE.coal, 0.26 * smoke * (1 - smoke) * 4)
                this.drawCircle(Math.cos(a) * r * 0.3 * smoke, Math.sin(a) * r * 0.2 * smoke - 16 * smoke,
                    r * (0.12 + 0.18 * smoke))
                this.endFill()
            }
        }
    }
}
